// @file Composable for managing whiteboard clipboard position context
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '@@/bits/whiteboard_no_dependencies'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { createSharedComposable } from '@vueuse/core'
import { ref } from 'vue'

// When pasting within the same page (Cmd+V), the pasted content is offset by 10px top and left.
// When pasting to another page, content is placed at the cursor location.
// Right-click paste also uses the cursor location.
const useWhiteboardClipboardPosition = createSharedComposable(
  (): {
    setClipboardSource: () => void
    getPastePoint: () => { x: number; y: number } | undefined
  } => {
    const PASTE_POINT_OFFSET_IN_PX = 10
    const lastCopiedPageId = ref<string | null>(null)
    const lastCopiedSelectionBounds = ref<{ center: { x: number; y: number } } | null>(null)

    function setClipboardSource(): void {
      const { editor } = useTldrawEditor()

      lastCopiedPageId.value = editor?.getCurrentPage()?.id ?? null
      lastCopiedSelectionBounds.value = editor?.getSelectionPageBounds() ?? null
    }

    function getPastePoint(): { x: number; y: number } | undefined {
      const { editor } = useTldrawEditor()

      const currentPageId = editor?.getCurrentPage()?.id
      const selectionBounds = editor?.getSelectionPageBounds()
      const isSamePage = !!currentPageId && currentPageId === lastCopiedPageId.value

      // Helper function to calculate paste point with appropriate offset based on whether we're on the same page
      const calculatePastePointWithOffset = (center: { x: number; y: number }): { x: number; y: number } => ({
        x: center.x + (isSamePage ? PASTE_POINT_OFFSET_IN_PX : 0),
        y: center.y + (isSamePage ? PASTE_POINT_OFFSET_IN_PX : 0),
      })

      // The paste will have selection bounds if the user copies and presses Cmd+V
      // We prioritize this if statement as it is very intuitive for the user to expect the paste to be:
      // 1. At the same position when you paste to another slide
      // 2. Be slightly offset from the last copied selection bounds if you paste to the same slide
      if (selectionBounds?.center) {
        return calculatePastePointWithOffset(selectionBounds.center)
      }

      // This can happen if the user did a cut/copy and then pastes using the mouse.
      // When pasting via mouse, then there would be no selection bounds. We keep the last copied selection bounds as a fallback.
      if (lastCopiedSelectionBounds.value?.center) {
        return calculatePastePointWithOffset(lastCopiedSelectionBounds.value.center)
      }

      // This can happen if a user is copying from outside Sandbox (ex: copy text, do not select anything, and just paste immediately)
      return { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 }
    }

    return {
      setClipboardSource,
      getPastePoint,
    }
  },
)

export { useWhiteboardClipboardPosition }
